// Question text is authored as HTML: rich text plus math, either MathML from docx imports or Quill/KaTeX
// formula spans. The admin table shows it as sanitized HTML (so equations render) and sorts/searches on a
// plain-text form that decodes entities and keeps math tokens together.

const ALLOWED_TAGS = new Set([
  "p", "br", "div", "span", "b", "strong", "i", "em", "u", "s", "sub", "sup", "ul", "ol", "li",
  "table", "thead", "tbody", "tr", "td", "th", "img",
  "math", "semantics", "annotation", "mrow", "mi", "mn", "mo", "mtext", "mspace", "ms", "msup", "msub",
  "svg", "path", "line",
  "msubsup", "mfrac", "msqrt", "mroot", "munder", "mover", "munderover", "mtable", "mtr", "mtd",
  "mfenced", "menclose", "mpadded", "mphantom", "mstyle", "mmultiscripts", "mprescripts", "none",
]);
// Dropped together with their contents; anything else not allowed is unwrapped, keeping its text.
const DROPPED_TAGS = new Set(["script", "style", "iframe", "object", "embed", "link", "meta"]);
const ALLOWED_ATTRS = new Set([
  "viewBox", "preserveAspectRatio", "d", "x1", "y1", "x2", "y2", "width", "height", "aria-hidden",
  "class", "style", "src", "alt", "colspan", "rowspan", "data-value",
  "display", "mathvariant", "stretchy", "fence", "separator", "accent", "accentunder", "lspace", "rspace",
  "open", "close", "separators", "linethickness", "columnalign", "rowspacing", "columnspacing", "encoding",
]);
const BLOCK_TAGS = new Set(["p", "div", "br", "li", "tr", "ul", "ol", "table"]);

// Chrome's MathML defaults (serif math font, own size/weight) clash with the surrounding text, so
// equations inherit the text font. Single-letter variables (tagged .sia-var below) stay italic.
export const mathTextSx = {
  "& math": { fontFamily: "inherit", fontWeight: "inherit", fontSize: "1.05em", mathStyle: "compact" },
  "& math mi": { textTransform: "none", fontStyle: "normal" },
  "& math mi.sia-var": { fontStyle: "italic" },
  "& .katex": { fontSize: "1em" },
};

const parse = (html) => new DOMParser().parseFromString(`<body>${html || ""}</body>`, "text/html").body;

const isSafeUrl = (value) => /^(https?:|data:image\/|\/)/i.test(value.trim());
const isSafeStyle = (value) => !/url\s*\(|expression\s*\(|@import|javascript:/i.test(value);

// Word exports typed spaces inside an equation as tokens holding only &nbsp;. Each one renders as an
// extra gap next to the operator spacing MathML already adds, so they are dropped. A blank <mtext>
// between two text runs is a real word gap and stays.
const isMathFiller = (el) => {
  if (!["mtext", "mo", "mi", "mn", "ms"].includes(el.localName) || el.children.length) return false;
  if (!/^[\s ]*$/.test(el.textContent)) return false;
  return !(
    el.localName === "mtext" &&
    (el.previousElementSibling?.localName === "mtext" || el.nextElementSibling?.localName === "mtext")
  );
};

const cleanNode = (node) => {
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.COMMENT_NODE) {
      child.remove();
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const tag = child.localName;
    if (DROPPED_TAGS.has(tag)) {
      child.remove();
      return;
    }
    cleanNode(child);
    if (isMathFiller(child)) {
      child.remove();
      return;
    }
    if (!ALLOWED_TAGS.has(tag)) {
      child.replaceWith(...Array.from(child.childNodes));
      return;
    }
    // Single-letter identifiers are variables: the cell styles them italic in the text font.
    const isVariable =
      tag === "mi" && child.getAttribute("mathvariant") !== "normal" && /^\p{L}$/u.test(child.textContent.trim());
    Array.from(child.attributes).forEach(({ name, value }) => {
      const ok =
        ALLOWED_ATTRS.has(name) &&
        (name !== "src" || isSafeUrl(value)) &&
        (name !== "style" || isSafeStyle(value));
      if (!ok) child.removeAttribute(name);
    });
    if (isVariable) child.setAttribute("class", "sia-var");
  });
};

// HTML that is safe to inject into the table cell. Non-breaking spaces and entities are decoded by the parser.
export function sanitizeQuestionHtml(html) {
  if (!html) return "";
  const body = parse(html);
  cleanNode(body);
  return body.innerHTML;
}

const collectText = (node, out) => {
  if (node.nodeType === Node.TEXT_NODE) {
    out.push(node.nodeValue);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const tag = node.localName;
  if (DROPPED_TAGS.has(tag) || tag === "annotation") return;
  if (node.classList?.contains("ql-formula") && node.getAttribute("data-value")) {
    out.push(` ${node.getAttribute("data-value")} `);
    return;
  }
  const block = BLOCK_TAGS.has(tag);
  if (block) out.push(" ");
  // Math tokens are joined without spaces so f(x)=cos stays readable.
  node.childNodes.forEach((c) => collectText(c, out));
  if (block) out.push(" ");
};

// Plain text for sorting/searching: tags removed, entities decoded, whitespace collapsed.
export function questionPlainText(html) {
  if (!html) return "";
  const out = [];
  collectText(parse(html), out);
  return out.join("").replace(/[\s ]+/g, " ").trim();
}
